import { NOTE_CLASS } from "../ui/card";
import type {
  JournalLocationData,
  JournalEntry,
  PageInfo,
} from "@roll1/shared";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Map } from "lucide-react";
import { useI18n } from "../../i18n";

interface LocationFormProps {
  data: JournalLocationData;
  onChange: (partial: Partial<JournalLocationData>) => void;
  focusedFieldRef: React.MutableRefObject<string | null>;
  entries?: JournalEntry[];
  pages?: PageInfo[];
  onNavigateToPage?: (pageId: string) => void;
}

export default function LocationForm({
  data,
  onChange,
  focusedFieldRef,
  entries,
  pages,
  onNavigateToPage,
}: LocationFormProps) {
  const { t } = useI18n();

  const handleFocus = (field: string) => {
    focusedFieldRef.current = field;
  };
  const handleBlur = () => {
    focusedFieldRef.current = null;
  };

  const locationEntries = entries?.filter((e) => e.type === "location") ?? [];

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.location.image")}
        </Label>
        <Input
          className="h-8 text-ui"
          value={data.image ?? ""}
          onChange={(e) => onChange({ image: e.target.value })}
          onFocus={() => handleFocus("image")}
          onBlur={handleBlur}
        />
      </div>
      <div>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.location.parent")}
        </Label>
        <Select
          value={data.parentEntryId ?? "__none__"}
          onValueChange={(v) =>
            onChange({ parentEntryId: v === "__none__" ? undefined : v })
          }
        >
          <SelectTrigger className="h-8 text-ui">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">{t("common.none")}</SelectItem>
            {locationEntries.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.location.description")}
        </Label>
        <Textarea
          className="text-ui min-h-[80px]"
          value={data.description}
          onChange={(e) => onChange({ description: e.target.value })}
          onFocus={() => handleFocus("description")}
          onBlur={handleBlur}
        />
      </div>
      <div className={NOTE_CLASS}>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.location.secrets")}
          <span className="ml-1 text-warn text-label">
            {t("journal.editor.gmOnly")}
          </span>
        </Label>
        <Textarea
          className="text-ui min-h-[60px]"
          value={data.secrets}
          onChange={(e) => onChange({ secrets: e.target.value })}
          onFocus={() => handleFocus("secrets")}
          onBlur={handleBlur}
        />
      </div>
      {pages && pages.length > 0 && (
        <div>
          <Label className="text-meta text-muted-foreground">
            {t("journal.form.location.page")}
          </Label>
          <Select
            value={data.pageId ?? "__none__"}
            onValueChange={(v) =>
              onChange({ pageId: v === "__none__" ? undefined : v })
            }
          >
            <SelectTrigger className="h-8 text-ui">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">{t("common.none")}</SelectItem>
              {pages.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {data.pageId &&
            onNavigateToPage &&
            (pages.find((p) => p.id === data.pageId) ? (
              <Button
                variant="ghost"
                size="sm"
                className="mt-1"
                onClick={() => onNavigateToPage(data.pageId!)}
              >
                <Map size={14} className="mr-1" />
                {t("journal.map.goToMap")}
              </Button>
            ) : (
              <p className="text-meta text-warn mt-1">
                {t("journal.map.pageNotFound")}
              </p>
            ))}
        </div>
      )}
    </div>
  );
}
